import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getCompetitors, createCompetitor, updateCompetitor, deleteCompetitor, getRooms } from "../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Trash2, Edit2, ExternalLink, Swords, TrendingDown, TrendingUp, Minus, Search, Building2, Star, MapPin, RefreshCw, Crown, Target, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hoteles importantes a destacar
const PRIORITY_HOTELS = ["medium", "sunway"];
const PREMIUM_HOTELS = ["me sitges", "mim sitges", "dolce"];

const getHotelCategory = (hotelName) => {
  const name = hotelName.toLowerCase();
  if (PRIORITY_HOTELS.some(h => name.includes(h))) return "priority";
  if (PREMIUM_HOTELS.some(h => name.includes(h))) return "premium";
  return "standard";
};

const CompetitorForm = ({ competitor, rooms, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: competitor?.name || "",
    website: competitor?.website || "",
    room_prices: competitor?.room_prices || {},
  });

  const handlePriceChange = (roomId, price) => {
    setFormData((prev) => ({
      ...prev,
      room_prices: {
        ...prev.room_prices,
        [roomId]: parseFloat(price) || 0,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre del Hotel</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Hotel Competidor"
          required
          data-testid="competitor-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="website">Sitio Web (opcional)</Label>
        <Input
          id="website"
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://..."
          data-testid="competitor-website-input"
        />
      </div>

      <div className="space-y-3">
        <Label>Precios por Habitación</Label>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {rooms.map((room) => (
            <div key={room.id} className="flex items-center gap-3">
              <span className="flex-1 text-sm truncate">{room.name}</span>
              <div className="relative w-28">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                <Input
                  type="number"
                  className="pl-7"
                  value={formData.room_prices[room.id] || ""}
                  onChange={(e) => handlePriceChange(room.id, e.target.value)}
                  placeholder="0"
                  data-testid={`competitor-price-${room.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" className="flex-1" data-testid="save-competitor-btn">
          Guardar
        </Button>
      </div>
    </form>
  );
};

const PriceComparison = ({ ourPrice, competitorPrice }) => {
  if (!competitorPrice || competitorPrice === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  const diff = ourPrice - competitorPrice;
  const diffPercent = ((diff / competitorPrice) * 100).toFixed(1);

  if (diff < 0) {
    return (
      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
        <TrendingDown className="w-4 h-4" />
        {Math.abs(diffPercent)}% menor
      </span>
    );
  } else if (diff > 0) {
    return (
      <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
        <TrendingUp className="w-4 h-4" />
        {diffPercent}% mayor
      </span>
    );
  } else {
    return (
      <span className="flex items-center gap-1 text-muted-foreground">
        <Minus className="w-4 h-4" />
        Igual
      </span>
    );
  }
};

const Competitors = () => {
  const [competitors, setCompetitors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  
  // Booking search state
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [yourPrice, setYourPrice] = useState(95);
  const [yourEstimatedPrice, setYourEstimatedPrice] = useState(95);
  const [basePrice, setBasePrice] = useState(95);

  // Calculate season multiplier for a given date
  const getSeasonMultiplier = (dateStr) => {
    if (!dateStr) return 1.0;
    const date = new Date(dateStr);
    const month = date.getMonth() + 1; // 1-12
    const dayOfWeek = date.getDay(); // 0=Sunday, 6=Saturday
    
    // Season multiplier
    let seasonMult = 1.0;
    if (month === 7 || month === 8) {
      seasonMult = 1.8; // Summer high season
    } else if (month === 6 || month === 9) {
      seasonMult = 1.4; // Summer shoulder
    } else if (month === 12 || month === 1) {
      seasonMult = 1.3; // Christmas/New Year
    } else if (month >= 3 && month <= 5) {
      seasonMult = 1.1; // Spring
    } else {
      seasonMult = 0.9; // Low season
    }
    
    // Weekend multiplier (Friday, Saturday, Sunday)
    const weekendMult = (dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6) ? 1.15 : 1.0;
    
    return seasonMult * weekendMult;
  };

  const fetchData = async () => {
    try {
      const [compRes, roomsRes] = await Promise.all([getCompetitors(), getRooms()]);
      setCompetitors(compRes.data);
      setRooms(roomsRes.data);
      
      // Calculate average price from rooms
      if (roomsRes.data.length > 0) {
        const avg = roomsRes.data.reduce((sum, r) => sum + (r.current_price || 0), 0) / roomsRes.data.length;
        setBasePrice(Math.round(avg));
        setYourPrice(Math.round(avg));
        setYourEstimatedPrice(Math.round(avg));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  // Update estimated price when checkin date changes
  useEffect(() => {
    if (checkin && basePrice) {
      const multiplier = getSeasonMultiplier(checkin);
      const estimated = Math.round(basePrice * multiplier);
      setYourEstimatedPrice(estimated);
      setYourPrice(estimated);
    }
  }, [checkin, basePrice]);

  const searchBookingPrices = async () => {
    setSearchLoading(true);
    
    // Update estimated price for the search date
    if (checkin) {
      const multiplier = getSeasonMultiplier(checkin);
      const estimated = Math.round(basePrice * multiplier);
      setYourEstimatedPrice(estimated);
      setYourPrice(estimated);
    }
    
    try {
      const res = await axios.get(`${API}/competitors/search`, {
        params: { 
          destination: "Sitges",
          checkin,
          checkout
        }
      });
      
      if (res.data.success) {
        setSearchResults(res.data);
        toast.success(`Encontrados ${res.data.hotels_found} hoteles`);
      } else {
        toast.error("Error al buscar precios");
      }
    } catch (error) {
      toast.error("Error al buscar precios");
    } finally {
      setSearchLoading(false);
    }
  };

  const getPositionBadge = (price) => {
    if (price < yourPrice) {
      return <Badge variant="destructive" className="ml-2"><TrendingDown className="w-3 h-3 mr-1" />Más barato</Badge>;
    } else if (price > yourPrice) {
      return <Badge className="bg-emerald-500 ml-2"><TrendingUp className="w-3 h-3 mr-1" />Más caro</Badge>;
    }
    return <Badge variant="secondary" className="ml-2">Similar</Badge>;
  };

  useEffect(() => {
    // Set default dates
    const today = new Date();
    const futureDate = new Date(today.setDate(today.getDate() + 30));
    const nextDay = new Date(futureDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setCheckin(futureDate.toISOString().split('T')[0]);
    setCheckout(nextDay.toISOString().split('T')[0]);
    
    fetchData();
  }, []);

  const handleSave = async (data) => {
    try {
      if (editingCompetitor) {
        await updateCompetitor(editingCompetitor.id, data);
        toast.success("Competidor actualizado");
      } else {
        await createCompetitor(data);
        toast.success("Competidor añadido");
      }
      setDialogOpen(false);
      setEditingCompetitor(null);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar competidor");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este competidor?")) return;
    try {
      await deleteCompetitor(id);
      toast.success("Competidor eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar competidor");
    }
  };

  const openEdit = (competitor) => {
    setEditingCompetitor(competitor);
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingCompetitor(null);
    setDialogOpen(true);
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="competitors-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Competencia</h1>
          <p className="text-muted-foreground">Monitorea precios de hoteles en Sitges</p>
        </div>
      </div>

      <Tabs defaultValue="booking" className="space-y-4">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="booking">Precios Booking</TabsTrigger>
          <TabsTrigger value="manual">Mis Competidores</TabsTrigger>
        </TabsList>

        {/* Booking Search Tab */}
        <TabsContent value="booking" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Buscar Precios en Sitges
              </CardTitle>
              <CardDescription>
                Consulta precios de hoteles en Booking.com
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="checkin">Check-in</Label>
                  <Input 
                    id="checkin"
                    type="date" 
                    value={checkin} 
                    onChange={(e) => setCheckin(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="checkout">Check-out</Label>
                  <Input 
                    id="checkout"
                    type="date" 
                    value={checkout} 
                    onChange={(e) => setCheckout(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="yourprice">
                    Tu precio estimado (€)
                    {checkin && (
                      <span className="text-xs text-muted-foreground ml-1">
                        (ajustado x{getSeasonMultiplier(checkin).toFixed(2)})
                      </span>
                    )}
                  </Label>
                  <Input 
                    id="yourprice"
                    type="number" 
                    value={yourPrice} 
                    onChange={(e) => setYourPrice(Number(e.target.value))}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={searchBookingPrices} disabled={searchLoading} className="w-full">
                    {searchLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Buscar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {searchResults && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Hoteles</p>
                    <p className="text-2xl font-bold">{searchResults.hotels_found}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Precio medio</p>
                    <p className="text-2xl font-bold">
                      €{searchResults.hotels?.length 
                        ? Math.round(searchResults.hotels.reduce((sum, h) => sum + h.price, 0) / searchResults.hotels.length)
                        : 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Tu precio estimado</p>
                    <p className="text-2xl font-bold text-blue-600">€{yourPrice}</p>
                    {checkin && (
                      <p className="text-xs text-muted-foreground">
                        Base €{basePrice} × {getSeasonMultiplier(checkin).toFixed(2)}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card className={yourPrice < (searchResults.hotels?.length ? searchResults.hotels.reduce((sum, h) => sum + h.price, 0) / searchResults.hotels.length : 0) 
                  ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/10" 
                  : "border-amber-400 bg-amber-50 dark:bg-amber-900/10"}>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Posición</p>
                    <p className={`text-xl font-bold ${yourPrice < 150 ? "text-emerald-600" : "text-amber-600"}`}>
                      Competitivo
                    </p>
                  </CardContent>
                </Card>
              </div>

              {searchResults.note && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200">ℹ️ {searchResults.note}</p>
                </div>
              )}

              {/* Hotel List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Hoteles en Sitges
                  </CardTitle>
                  <CardDescription>
                    <span className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Target className="w-4 h-4 text-orange-500" />
                        <span className="text-sm">Competencia directa</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Crown className="w-4 h-4 text-purple-500" />
                        <span className="text-sm">Premium</span>
                      </span>
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {searchResults.hotels
                      ?.sort((a, b) => {
                        // Ordenar: primero priority, luego por precio
                        const catA = getHotelCategory(a.name);
                        const catB = getHotelCategory(b.name);
                        if (catA === "priority" && catB !== "priority") return -1;
                        if (catB === "priority" && catA !== "priority") return 1;
                        return a.price - b.price;
                      })
                      .map((hotel, idx) => {
                        const category = getHotelCategory(hotel.name);
                        const isPriority = category === "priority";
                        const isPremium = category === "premium";
                        
                        return (
                      <div 
                        key={idx}
                            className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                              isPriority
                                ? "bg-orange-50 dark:bg-orange-900/20 border-orange-300 ring-2 ring-orange-200"
                                : hotel.price < yourPrice 
                            ? "bg-red-50 dark:bg-red-900/10 border-red-200" 
                            : hotel.price > yourPrice 
                              ? "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200"
                              : "bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isPriority 
                                  ? "bg-orange-100 dark:bg-orange-900/30" 
                                  : isPremium 
                                    ? "bg-purple-100 dark:bg-purple-900/30"
                                    : "bg-primary/10"
                              }`}>
                                {isPriority ? (
                                  <Target className="w-5 h-5 text-orange-600" />
                                ) : isPremium ? (
                                  <Crown className="w-5 h-5 text-purple-600" />
                                ) : (
                            <Building2 className="w-5 h-5 text-primary" />
                                )}
                          </div>
                          <div>
                                <h4 className={`font-semibold ${isPriority ? "text-orange-700 dark:text-orange-400" : ""}`}>
                                  {hotel.name}
                                  {isPriority && (
                                    <Badge className="ml-2 bg-orange-500 text-white text-xs">IMPORTANTE</Badge>
                                  )}
                                </h4>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-amber-500" />
                                {hotel.rating}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {hotel.distance}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                              <p className={`text-xl font-bold ${isPriority ? "text-orange-600" : ""}`}>€{hotel.price}</p>
                          {getPositionBadge(hotel.price)}
                              {isPriority && hotel.price < yourPrice && (
                                <p className="text-xs text-orange-600 mt-1 font-medium">⚠️ Por debajo de ti</p>
                              )}
                        </div>
                      </div>
                        );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Manual Competitors Tab */}
        <TabsContent value="manual" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="add-competitor-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Competidor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingCompetitor ? "Editar Competidor" : "Nuevo Competidor"}</DialogTitle>
                </DialogHeader>
                <CompetitorForm
                  competitor={editingCompetitor}
                  rooms={rooms}
                  onSave={handleSave}
                  onClose={() => setDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
          
          {competitors.length === 0 ? (
        <Card className="p-12 text-center">
          <Swords className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No hay competidores manuales</h3>
          <p className="text-muted-foreground mb-4">
            Añade competidores para comparar precios específicos.
          </p>
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" />
            Añadir Competidor
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Competitor Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitors.map((competitor) => (
              <Card key={competitor.id} data-testid={`competitor-card-${competitor.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{competitor.name}</CardTitle>
                      {competitor.website && (
                        <a
                          href={competitor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ver web
                        </a>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(competitor)}
                        data-testid={`edit-competitor-${competitor.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(competitor.id)}
                        data-testid={`delete-competitor-${competitor.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {Object.keys(competitor.room_prices || {}).length} precios registrados
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Price Comparison Table */}
          <Card>
            <CardHeader>
              <CardTitle>Comparación de Precios</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Habitación</TableHead>
                      <TableHead className="text-right">Tu Precio</TableHead>
                      {competitors.map((c) => (
                        <TableHead key={c.id} className="text-right">{c.name}</TableHead>
                      ))}
                      <TableHead className="text-right">Posición</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rooms.map((room) => {
                      const compPrices = competitors.map((c) => c.room_prices?.[room.id] || 0).filter(p => p > 0);
                      const avgCompPrice = compPrices.length > 0 
                        ? compPrices.reduce((a, b) => a + b, 0) / compPrices.length 
                        : null;
                      
                      return (
                        <TableRow key={room.id}>
                          <TableCell className="font-medium">{room.name}</TableCell>
                          <TableCell className="text-right font-mono">€{room.current_price}</TableCell>
                          {competitors.map((c) => (
                            <TableCell key={c.id} className="text-right font-mono">
                              {c.room_prices?.[room.id] ? `€${c.room_prices[room.id]}` : "-"}
                            </TableCell>
                          ))}
                          <TableCell className="text-right">
                            <PriceComparison ourPrice={room.current_price} competitorPrice={avgCompPrice} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Competitors;
